/**
 * BackgroundJobsPanel — shows active and recent Cortex background jobs.
 *
 * Accessible from the chat sidebar. Displays job status with View/Retry actions.
 * Uses useCortexBackgroundJobs for data and live updates via Supabase Realtime.
 */

import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { cn } from '@flowstate/utils';
import {
  useCortexBackgroundJobs,
  type CortexBackgroundJob,
  type WorkflowGate,
} from "../../hooks/useCortexBackgroundJobs";
import { submitCortexBackgroundJob } from "@flowstate/quick-actions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import {
  CheckCircle2,
  Clock,
  XCircle,
  Zap,
  RefreshCw,
  Eye,
  Loader2,
  ShieldQuestion,
  Radio,
  Check,
  X,
} from "lucide-react";

interface BackgroundJobsPanelProps {
  /** Callback to insert a result message into the chat */
  onViewResult?: (job: CortexBackgroundJob) => void;
  className?: string;
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "just now";
}

function getElapsedTime(createdAt: string): string {
  const seconds = Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s elapsed`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s elapsed`;
}

function JobStatusIcon({ status }: { status: CortexBackgroundJob["status"] }) {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />;
    case "failed":
      return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
    case "processing":
      return <Loader2 className="h-4 w-4 text-blue-500 shrink-0 animate-spin" />;
    case "awaiting_review":
      return <ShieldQuestion className="h-4 w-4 text-amber-500 shrink-0" />;
    case "awaiting_event":
      return <Radio className="h-4 w-4 text-violet-500 shrink-0" />;
    case "queued":
      return <Clock className="h-4 w-4 text-amber-500 shrink-0" />;
    default:
      return <Clock className="h-4 w-4 text-muted-foreground shrink-0" />;
  }
}

function JobRow({
  job,
  gate,
  onView,
  onRetry,
  onResolveGate,
  resolving,
}: {
  job: CortexBackgroundJob;
  gate?: WorkflowGate;
  onView?: (job: CortexBackgroundJob) => void;
  onRetry?: (job: CortexBackgroundJob) => void;
  onResolveGate?: (gate: WorkflowGate, verdict: "approved" | "rejected") => void;
  resolving?: boolean;
}) {
  const workflowRun = job.context?.workflowRun;
  const displayLabel = workflowRun?.workflowName
    ? `Workflow: ${workflowRun.workflowName}`
    : (job.context?.prompt ?? job.intent);
  const promptPreview =
    displayLabel.length > 55 ? `${displayLabel.slice(0, 55)}...` : displayLabel;
  const awaitingReview = job.status === "awaiting_review" && gate?.kind === "human_review";

  return (
    <div className="flex items-start gap-3 px-3 py-2.5 border-b border-border/10 last:border-0 hover:bg-muted/30 transition-colors">
      <JobStatusIcon status={job.status} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{promptPreview}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {job.status === "completed" && (
            <span className="text-[11px] text-muted-foreground">
              {formatTimeAgo(job.processed_at ?? job.created_at)}
            </span>
          )}
          {job.status === "failed" && job.error && (
            <span className="text-[11px] text-red-500 truncate max-w-[200px]">
              {job.error}
            </span>
          )}
          {(job.status === "processing" || job.status === "queued") && (
            <span className="text-[11px] text-muted-foreground">
              {job.status === "processing" ? "running" : "queued"} ·{" "}
              {getElapsedTime(job.created_at)}
            </span>
          )}
          {awaitingReview && (
            <span className="text-[11px] text-amber-600 dark:text-amber-400">
              awaiting review · {formatTimeAgo(gate.created_at)}
            </span>
          )}
          {job.status === "awaiting_event" && (
            <span className="text-[11px] text-violet-500">
              waiting for {gate?.domain && gate?.event_type
                ? `${gate.domain}.${gate.event_type}`
                : "event"}
            </span>
          )}
          {job.specialist_used && (
            <span className="text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
              {job.specialist_used}
            </span>
          )}
          {workflowRun && job.status === "processing" && (
            <span className="text-[10px] text-blue-500 bg-blue-500/10 px-1.5 py-0.5 rounded">
              workflow
            </span>
          )}
        </div>

        {/* Human review gate — the workflow is suspended on a durable
            promise; this verdict settles it and resumes (or fails) the run. */}
        {awaitingReview && (
          <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2">
            <p className="text-[11px] font-medium text-amber-700 dark:text-amber-300">
              {gate.label}
            </p>
            {gate.reason && (
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {gate.reason}
              </p>
            )}
            <div className="flex items-center gap-2 mt-2">
              <Button
                size="sm"
                className="h-7 px-2.5 text-xs bg-green-600 hover:bg-green-700 text-white"
                disabled={resolving}
                onClick={() => onResolveGate?.(gate, "approved")}
              >
                <Check className="h-3 w-3 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2.5 text-xs border-red-500/40 text-red-500 hover:bg-red-500/10"
                disabled={resolving}
                onClick={() => onResolveGate?.(gate, "rejected")}
              >
                <X className="h-3 w-3 mr-1" />
                Reject
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {job.status === "completed" && onView && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onView(job)}
            aria-label="View result"
          >
            <Eye className="h-3 w-3 mr-1" />
            View
          </Button>
        )}
        {job.status === "failed" && onRetry && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-red-500 hover:text-red-600"
            onClick={() => onRetry(job)}
            aria-label="Retry job"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        )}
      </div>
    </div>
  );
}

export function BackgroundJobsPanel({
  onViewResult,
  className,
}: BackgroundJobsPanelProps) {
  const { jobs, pendingGates, isLoading, refetch } = useCortexBackgroundJobs({ limit: 10 });
  const [resolvingGateId, setResolvingGateId] = React.useState<string | null>(null);

  /**
   * Settle a human_review gate — POST {op:"resolve"} to workflow-runner with
   * the user's session JWT (functions.invoke attaches it). The runner checks
   * gate ownership server-side, settles the latent promise, and the suspended
   * run resumes (approved) or fails (rejected).
   */
  const handleResolveGate = useCallback(
    async (gate: WorkflowGate, verdict: "approved" | "rejected") => {
      setResolvingGateId(gate.id);
      try {
        const { data, error } = await supabase.functions.invoke("workflow-runner", {
          body: { op: "resolve", gateId: gate.id, verdict },
        });
        if (error) {
          toast.error("Review failed", { description: error.message });
          return;
        }
        if ((data as { ok?: boolean } | null)?.ok !== true) {
          toast.error("Review failed", {
            description: (data as { error?: string } | null)?.error ?? "Unexpected response",
          });
          return;
        }
        toast.success(
          verdict === "approved" ? "✅ Approved — workflow resuming" : "Workflow rejected",
        );
        refetch();
      } catch (err) {
        toast.error("Review failed", {
          description: err instanceof Error ? err.message : "Unknown error",
        });
      } finally {
        setResolvingGateId(null);
      }
    },
    [refetch],
  );

  const handleRetry = useCallback(
    async (job: CortexBackgroundJob) => {
      const prompt = job.context?.prompt;
      if (!prompt) {
        toast.error("Cannot retry — original prompt not available");
        return;
      }

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        if (!session?.user?.id) {
          toast.error("Sign in required");
          return;
        }

        const orgId = job.org_id ?? null;

        const queueResult = await submitCortexBackgroundJob({
          supabase,
          userId: session.user.id,
          orgId,
          prompt,
          specialist: job.context?.specialist ?? undefined,
          source: "cortex-background-job-retry",
          retryOf: job.id,
        });

        if (queueResult.error) {
          toast.error("Retry failed", { description: queueResult.error });
          return;
        }

        toast.success("⚡ Job re-queued", {
          description: "Cortex will retry this task.",
        });
        refetch();
      } catch (err) {
        toast.error("Retry failed", {
          description: err instanceof Error ? err.message : "Unknown error",
        });
      }
    },
    [refetch]
  );

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/20">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-500" />
          <span className="text-sm font-semibold">Background Jobs</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => refetch()}
          aria-label="Refresh jobs"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Job list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && jobs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <Zap className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No background jobs yet</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Toggle "Background" mode in the chat input to submit jobs
            </p>
          </div>
        )}

        {!isLoading &&
          jobs.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              gate={pendingGates[job.id]}
              onView={onViewResult}
              onRetry={handleRetry}
              onResolveGate={handleResolveGate}
              resolving={resolvingGateId === pendingGates[job.id]?.id}
            />
          ))}
      </div>
    </div>
  );
}
